import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Checkbox, Col, Form, Popconfirm, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteCalcSettings, useUpdateCalcSettings } from "../../../../../common/api/queries/calcSettings";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import PopconfirmDeleteIcon from "../../../../../common/components/icons/PopconfirmDeleteIcon";
import { rowGutter } from "../../../../../common/constants";
import InstitutionSelect from "../../../../enumerations/components/form/InstitutionSelect";
import { InstitutionType } from "../../../../institution/enums";
import { CalcType } from "../../../enums";
import { ADMIN_CALC_ROUTE_PATHS } from "../../paths";
import { CalcSettings, UpdateCalcSettings } from "../../types";
import { convertCalcSettingsToUpdateObject } from "../../utils";
import CalcSettingsAttachmentConfsPart from "./parts/CalcSettingsAttachmentConfsPart";
import { CalcSettingsPaymentDetailsPart } from "./parts/CalcSettingsPaymentDetailsPart";
import CalcSettingsProductsPart from "./parts/CalcSettingsProductsPart";

const colSpan = 5;

interface Props {
  calcType: CalcType;
  calcSettings: CalcSettings;
}

export const UpdateCalcSettingsForm = ({ calcSettings, calcType }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm<UpdateCalcSettings>();

  const updateCalcSettings = useUpdateCalcSettings();
  const deleteCalcSettings = useDeleteCalcSettings();

  useEffect(() => {
    const formData = convertCalcSettingsToUpdateObject(calcSettings);

    form.setFieldsValue(formData);
  }, [calcSettings]);

  const handleFormFinish = (values: UpdateCalcSettings) => {
    updateCalcSettings.mutate(
      { id: calcSettings.id, object: values },
      { onError: (error) => setErrorsToForm(form, "calc.settings.attrs", error.violations) },
    );
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFormFinish} name="calcSettingsForm">
      <HiddenInput name="type" initialValue={calcType} />
      <HiddenInput name="optimisticLockVersion" />

      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={calcSettings} />

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <InstitutionSelect
            formItemProps={{
              name: "institutionId",
              label: t("common.insuranceInstitution"),
              rules: [validations.notNull],
            }}
            optionsProps={{
              selected: calcSettings ? [calcSettings?.institution] : undefined,
              filterType: InstitutionType.INSURANCE_COMPANY,
            }}
          />
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="enabled"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox>{t("calc.settings.attrs.enabled")}</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <CalcSettingsProductsPart calcType={calcType} />

      <CalcSettingsPaymentDetailsPart />

      <CalcSettingsAttachmentConfsPart calcSettings={calcSettings} />

      <div className="margin-top-medium">
        <Button type="primary" htmlType="submit" className="margin-right-tiny" icon={<AntIcon type="save" />}>
          {t("common.save")}
        </Button>

        <Popconfirm
          title={t("calc.settings.titles.deleteConfirm")}
          icon={<PopconfirmDeleteIcon />}
          okText={t("common.yes")}
          cancelText={t("common.no")}
          okType="danger"
          onConfirm={() =>
            deleteCalcSettings.mutate(calcSettings.id, {
              onSuccess: () => navigate(ADMIN_CALC_ROUTE_PATHS.list.to, { replace: true }),
            })
          }
        >
          <Button icon={<AntIcon type="delete" />} danger>
            {t("common.delete")}
          </Button>
        </Popconfirm>
      </div>
    </Form>
  );
};
